import React, { useCallback, useState } from "react";
import { Stage, Layer, Image as KonvaImage, Line, Circle, Rect, Group } from "react-konva";
import useImage from "use-image";
import { Button } from "../../primary";
import style from "./WoundTracer.module.scss";

const CLOSE_RADIUS = 14;
const ANCHOR_SIZE = 7;
const STROKE_COLOR = "#00c875";
const FILL_COLOR = "rgba(0,200,117,0.25)";
const START_COLOR = "#1a6cf5";
const ANCHOR_COLOR = "#ffffff";

function dist(a, b) {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

function computeLetterbox(actualW, actualH, stageW, stageH) {
  const imageAspect = actualW / actualH;
  const stageAspect = stageW / stageH;
  let imgW, imgH;
  if (imageAspect > stageAspect) {
    imgW = stageW;
    imgH = stageW / imageAspect;
  } else {
    imgH = stageH;
    imgW = stageH * imageAspect;
  }
  return {
    imgX: (stageW - imgW) / 2,
    imgY: (stageH - imgH) / 2,
    imgW,
    imgH,
  };
}

function toNormalisedPoints(pts, imgX, imgY, imgW, imgH) {
  return pts.map((p) => [(p.x - imgX) / imgW, (p.y - imgY) / imgH]);
}

function insideImage(pos, imgX, imgY, imgW, imgH) {
  return pos.x >= imgX && pos.x <= imgX + imgW && pos.y >= imgY && pos.y <= imgY + imgH;
}

function PolygonOverlay({ points, closed, mousePos }) {
  if (points.length === 0) return null;

  const flatPoints = points.flatMap((p) => [p.x, p.y]);
  const previewLine = !closed && mousePos ? [...flatPoints, mousePos.x, mousePos.y] : flatPoints;
  const nearStart = !closed && mousePos && points.length >= 3 ? dist(points[0], mousePos) < CLOSE_RADIUS : false;

  return (
    <Group>
      <Line
        points={previewLine}
        closed={closed}
        stroke={STROKE_COLOR}
        strokeWidth={2}
        fill={closed ? FILL_COLOR : "transparent"}
        lineCap="round"
        lineJoin="round"
        listening={false}
      />
      {nearStart && (
        <Circle x={points[0].x} y={points[0].y} radius={CLOSE_RADIUS} stroke="rgba(26,108,245,0.6)" strokeWidth={1.5} listening={false} />
      )}
      {points.map((p, i) => (
        <Rect
          key={i}
          x={p.x - ANCHOR_SIZE}
          y={p.y - ANCHOR_SIZE}
          width={ANCHOR_SIZE * 2}
          height={ANCHOR_SIZE * 2}
          fill={i === 0 && !closed ? START_COLOR : ANCHOR_COLOR}
          stroke="#888"
          strokeWidth={1}
        />
      ))}
    </Group>
  );
}

function SavedPolygons({ polygons }) {
  return (
    <>
      {polygons.map((poly, idx) => (
        <Group key={idx}>
          <Line points={poly.flatMap((p) => [p.x, p.y])} closed stroke={STROKE_COLOR} strokeWidth={2} fill={FILL_COLOR} listening={false} />
          {poly.map((p, i) => (
            <Rect
              key={i}
              x={p.x - ANCHOR_SIZE}
              y={p.y - ANCHOR_SIZE}
              width={ANCHOR_SIZE * 2}
              height={ANCHOR_SIZE * 2}
              fill={ANCHOR_COLOR}
              stroke="#888"
              strokeWidth={1}
            />
          ))}
        </Group>
      ))}
    </>
  );
}

export default function WoundTracer({ imageSrc, isFallback = false, onExit, onClear, width = 540, height = 360 }) {
  const [image] = useImage(imageSrc, "anonymous");

  const [current, setCurrent] = useState([]);
  const [closed, setClosed] = useState(false);
  const [polygons, setPolygons] = useState([]);
  const [mousePos, setMousePos] = useState(null);
  const [dragging, setDragging] = useState(null);

  const actualW = image?.naturalWidth || width;
  const actualH = image?.naturalHeight || height;

  const { imgX, imgY, imgW, imgH } = computeLetterbox(actualW, actualH, width, height);

  const handleMouseMove = useCallback(
    (e) => {
      const pos = e.target.getStage().getPointerPosition();
      if (!pos) return;
      if (dragging !== null) {
        const clamped = {
          x: Math.max(imgX, Math.min(imgX + imgW, pos.x)),
          y: Math.max(imgY, Math.min(imgY + imgH, pos.y)),
        };
        setCurrent((prev) => {
          const updated = [...prev];
          updated[dragging] = clamped;
          return updated;
        });
        return;
      }
      setMousePos(insideImage(pos, imgX, imgY, imgW, imgH) ? pos : null);
    },
    [dragging, imgX, imgY, imgW, imgH]
  );

  const handleClick = useCallback(
    (e) => {
      if (isFallback) return;

      const pos = e.target.getStage().getPointerPosition();

      if (!pos || !insideImage(pos, imgX, imgY, imgW, imgH)) return;

      if (closed) {
        setPolygons((prev) => [...prev, current]);
        setCurrent([pos]);
        setClosed(false);
        setMousePos(pos);
        return;
      }

      if (current.length >= 3 && dist(current[0], pos) < CLOSE_RADIUS) {
        setClosed(true);
        setMousePos(null);
        return;
      }

      setCurrent((prev) => [...prev, pos]);
    },
    [isFallback, closed, current, imgX, imgY, imgW, imgH]
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (!closed) return;
      const pos = e.target.getStage().getPointerPosition();
      if (!pos) return;
      const idx = current.findIndex((p) => dist(p, pos) <= ANCHOR_SIZE + 4);
      if (idx !== -1) setDragging(idx);
    },
    [closed, current]
  );

  const handleMouseUp = useCallback(() => setDragging(null), []);

  const handleSubmit = useCallback(() => {
    const allPolygons = closed && current.length >= 3 ? [...polygons, current] : polygons;

    if (allPolygons.length === 0 || !onExit) return;

    const regions = allPolygons.map((poly) => ({
      points: toNormalisedPoints(poly, imgX, imgY, imgW, imgH),
    }));

    onExit({
      images: [
        {
          src: imageSrc,
          regions,
          pixelSize: { w: actualW, h: actualH },
        },
      ],
    });
  }, [closed, current, polygons, onExit, imageSrc, imgX, imgY, imgW, imgH, actualW, actualH]);

  const handleClear = useCallback(() => {
    setPolygons([]);
    setCurrent([]);
    setClosed(false);
    setMousePos(null);
    if (onClear) onClear();
  }, [onClear]);

  const handleUndo = useCallback(() => {
    if (closed) {
      setClosed(false);
    } else if (current.length > 0) {
      setCurrent((prev) => prev.slice(0, -1));
    } else if (polygons.length > 0) {
      const last = polygons[polygons.length - 1];
      setPolygons((prev) => prev.slice(0, -1));
      setCurrent(last);
      setClosed(true);
    }
  }, [closed, current, polygons]);

  const isSubmitEnabled = polygons.length > 0 || (closed && current.length >= 3);

  const isUndoEnabled = current.length > 0 || closed || polygons.length > 0;

  const traceCount = polygons.length + (closed && current.length >= 3 ? 1 : 0);

  return (
    <div data-testid="wound-tracer" className={style.tracer_wrapper}>
      <div className={style.stage_container} data-testid="stage-container">
        <Stage
          width={width}
          height={height}
          className={`${style.stage_canvas} ${isFallback ? style.stage_canvas_fallback : ""}`}
          onMouseMove={handleMouseMove}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          data-testid="konva-stage"
        >
          <Layer>
            {image && <KonvaImage image={image} x={imgX} y={imgY} width={imgW} height={imgH} listening={false} />}
            <SavedPolygons polygons={polygons} />
            <PolygonOverlay points={current} closed={closed} mousePos={mousePos} />
          </Layer>
        </Stage>
      </div>

      <div className={style.controls} data-testid="wound-tracer-controls">
        <Button data-testid="btn-submit" disabled={!isSubmitEnabled} classes={{ className: `btn ${style.btn_add}` }} onClick={handleSubmit}>
          {traceCount > 1 ? `Submit (${traceCount})` : "Submit"}
        </Button>

        <Button data-testid="btn-clear" classes={{ className: `btn ${style.no_btn_clr}` }} onClick={handleClear}>
          Clear
        </Button>

        <Button data-testid="btn-undo" disabled={!isUndoEnabled} classes={{ className: `btn ${style.no_btn_clr}` }} onClick={handleUndo}>
          Undo
        </Button>
      </div>
    </div>
  );
}
